import React, { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Modal,
  Table
} from 'semantic-ui-react';
import axios from 'axios';

import { default as Configs } from '../lib/configs';
import { useError } from '../providers';

import CommandConfigBanned from './CommandConfigBanned';

const CommandConfig = ({ type, command, refresh }) => {
  const AdditionalConfig = Configs[command.original] || React.Fragment;

  const { showError } = useError();

  const calcIntMod = () => {
    if (interval % 3600 === 0) {
      setInterval(interval / 3600);
      return 3600;
    }
    if (interval % 60 === 0) {
      setInterval(interval / 60);
      return 60;
    }

    return 1;
  }

  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(Boolean(command.flags & 1));
  const [subOnly, setSubOnly] = useState(Boolean(command.flags & 2));
  const [limit, setLimit] = useState(command.limit);
  const [interval, setInterval] = useState(command.interval);
  const [intMod, setIntMod] = useState(calcIntMod());
  const [additionalConfig, setAdditionalConfig] = useState({});
  const [alias, setAlias] = useState(command.is_alias ? command.command : null);

  const enableSubOnly = (ev, data) => {
    setEnabled(Boolean(data.checked | enabled));
    setSubOnly(data.checked);
  };

  const intervals = [
    { key: 'seconds', text: 'seconds', value: 1 },
    { key: 'minutes', text: 'minutes', value: 60 },
    { key: 'hours', text: 'hours', value: 3600 },
  ]

  useEffect(() => {
    if (!limit) {
      setLimit(5);
    } else if (limit < 0) {
      setLimit(0);
    } else if (limit > 50) {
      setLimit(50);
    }

    if (!interval) {
      setInterval(5);
    } else if (interval < 0) {
      setInterval(0);
    } else if (interval > 60) {
      setInterval(60);
    }
  }, [limit, interval]);

  const saveCommandConfig = async () => {
    const endpoint = (type === 'custom') ? '/api/custom' : '/api/commands';
    let payload = {};
    if (type === 'custom') {
      payload = {
        'command': command.command,
        enabled,
        subOnly,
        limit,
        'interval': (interval * intMod),
        'config': additionalConfig
      }
    } else {
      payload = {
        'command': command.original,
        'alias': alias ? alias : null,
        enabled,
        subOnly,
        limit,
        'interval': (interval * intMod),
        'config': additionalConfig
      }
    }

    try {
      const { data } = await axios.post(
        endpoint,
        payload,
      );

      switch (data.status) {
        case 200: // Save was successful.
          setOpen(false);
          refresh();
          break;
        case 403: // Validation error
            showError(<center>
            <p>{data.response.message}</p>
            {Object.keys(data.response.errors).map(key =>
              <p>{data.response.errors[key]}</p>
            )}
          </center>);
          break;
        case 401: // Unauthorized; redirect to home.
            showError(<center>Unauthorized access.</center>, () => window.location.assign('/'));
            break;
        case 404: // Command not found
            showError(<center>{data.response.message}</center>, () => window.location.assign('/'));
            break;
        case 500: // Internal server
        default:  // Anything else
            showError(<center>An unknown error occured while trying to save your changes.</center>);
            break;
      }
    } catch(err) {
        showError(<center>An unknown error occured while trying to save your changes.</center>);
    }
  }

  const updateAdditionalConfig = (key, value) => {
    setAdditionalConfig(config => {
      return {
        ...config,
        [key]: value
      }
    })
  }

  return (
    <Modal
      onClose={() => { setOpen(false); refresh(); }}
      onOpen={() => setOpen(true)}
      open={open}
      closeOnDimmerClick={false}
      trigger={
        <Button icon disabled={type === 'global'}>
          <Icon name='configure' />
        </Button>
      }
    >
      <Modal.Header>Configuration for {type === 'custom' ? 'Custom Command ' : ''}!{command.command}</Modal.Header>
      <Modal.Content>
        <Table basic='very' inverted>
          <Table.Body>
            {command.is_alias ? <Table.Row>
                <Table.Cell colSpan={2}>This command is aliased from !{command.original}.</Table.Cell>
              </Table.Row>
            : ''}
            <Table.Row>
              <Table.Cell collapsing verticalAlign='top'><strong>Enabled</strong></Table.Cell>
              <Table.Cell><Form.Checkbox toggle checked={enabled} onChange={(ev, data) => setEnabled(data.checked)} /></Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell collapsing verticalAlign='top'><strong>Sub Only</strong></Table.Cell>
              <Table.Cell>
                <Form.Checkbox toggle checked={subOnly} onChange={enableSubOnly} />
                Enabling this will only allow subscribers of your channel to run the command.
              </Table.Cell>
            </Table.Row>
            { (command.type === 'global' || !('type' in command)) ? <Table.Row>
                <Table.Cell collapsing verticalAlign='top'><strong>Alias</strong></Table.Cell>
                <Table.Cell>
                  <Input value={alias} fluid onChange={(ev) => setAlias(ev.target.value)}/><br />
                  Give this command a custom name to be used on your stream, also useful for translations.<br />
                </Table.Cell>
              </Table.Row> : ''}
            <Table.Row>
              <Table.Cell collapsing verticalAlign='top'><strong>Rate Limit</strong></Table.Cell>
              <Table.Cell>
                Chatters can use this command up to{' '}
                <span class="ui input">
                  <input
                    type='number'
                    min='1'
                    max='50'
                    style={{
                      width: '75px',
                      margin: '0px 5px'
                    }}
                    value={limit}
                    onChange={(ev) => setLimit(parseInt(ev.target.value))}
                  />
                </span>{' '}
                times within{' '}
                <span class="ui input">
                  <input
                    type='number'
                    min='1'
                    max='60'
                    style={{
                      width: '75px',
                      margin: '0px 5px'
                    }}
                    value={interval}
                    onChange={(ev) => setInterval(parseInt(ev.target.value))}
                  />{' '}
                </span>
                <Dropdown
                  upward
                  floating
                  inline
                  options={intervals}
                  defaultValue={intMod}
                  onChange={(ev, { value }) => setIntMod(value)}
                />.
              </Table.Cell>
            </Table.Row>
            <CommandConfigBanned update={updateAdditionalConfig} config={command.config} params={command.params} />
            <AdditionalConfig update={updateAdditionalConfig} config={command.config} />
          </Table.Body>
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button color='black' onClick={() => { setOpen(false); }}>
          Cancel
        </Button>
        <Button color='green' onClick={saveCommandConfig}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  )
};

export default CommandConfig;
